"use client"

import { useEffect, useRef } from "react"
import { useInView } from "react-intersection-observer"

import { contentAnalytics, type EventProperties } from "@/lib/analytics"

/**
 * Fire a one-shot `affiliate_product_shown` impression the first time a product
 * card is meaningfully visible. Returns a ref to attach to the card root.
 *
 * Pair the SAME `surface` string with the card's click event
 * (`contentAnalytics.affiliateProductClicked(surface, …)`) so PostHog can join
 * impressions to clicks and compute CTR per surface. No-ops when `surface` is
 * undefined (nothing to attribute) or IntersectionObserver is unavailable.
 */
export function useAffiliateImpression(surface: string | undefined, props?: EventProperties) {
  const fired = useRef(false)
  const propsRef = useRef(props)
  propsRef.current = props
  const { ref, inView } = useInView({ threshold: 0.5, triggerOnce: true })

  useEffect(() => {
    if (!surface || fired.current || !inView) return
    fired.current = true
    contentAnalytics.affiliateProductShown(surface, propsRef.current)
  }, [inView, surface])

  return ref
}
